/**
 * Shared frontend order-link tier helper + colour map.
 *
 * Mirrors the backend contracts:
 *   - `OrderLink` interface     -> backend-v2/src/v2/orders/v2-orders.service.ts
 *   - `isDispatchedStage()`     -> backend-v2/src/common/utils/order-stage.util.ts
 *   - the restrictiveness/tier ranking in `orderLinkRestrictivenessRank()`
 *
 * Single source of truth (client-side) for the tier -> colour map. Consumed by
 * OrderLinkChip and the S2 surfaces (patient search, IV ready queue, etc.).
 */

/**
 * Frontend mirror of the backend `OrderLink` interface. Field names match the
 * backend exactly; `createdAt` arrives as an ISO string over the wire (backend
 * types it as `Date`).
 */
export interface OrderLink {
  orderNumber: string;
  orderStatus: string;
  workflowStage: string;
  rxNumber: string | null;
  deliveryDate: string | null;
  isDispatched: boolean;
  isCancelled: boolean;
  isArchived: boolean;
  createdAt: string;
}

export type OrderLinkTier = 'active' | 'in-flight' | 'completed' | 'cancelled';

// ⚠️ LOCKSTEP DUPLICATION — keep in sync with backend `isDispatchedStage()`
// (backend-v2/src/common/utils/order-stage.util.ts). The backend already sends
// `isDispatched` on every OrderLink, so this set is only a defensive fallback;
// flag any change here in the PR so both copies move together.
const DISPATCHED_STAGES: ReadonlySet<string> = new Set([
  'out_for_delivery',
  'delivery_missed',
  'delivered',
  'ready_for_pickup',
  'picked_up',
]);

// Terminal-completed = DELIVERED / PICKED_UP ONLY (mirrors backend
// `orderLinkRestrictivenessRank()`): the physical send is finished AND the row
// carries status 'completed', so it is exempt from the duplicate-guard unique
// index and override-able. Every other dispatched stage (out_for_delivery,
// delivery_missed, ready_for_pickup) is still OPEN — the row keeps status
// 'in_progress', stays indexed, and hard-BLOCKs a re-send.
const TERMINAL_COMPLETED_STAGES: ReadonlySet<string> = new Set(['delivered', 'picked_up']);

// Client mirror of backend `OrderStatus.COMPLETED` (order.entity.ts).
const COMPLETED_STATUS = 'completed';

// Terminal-completed needs BOTH halves, exactly like the backend rank
// (v2-orders.service.ts): the terminal stage AND status 'completed'. The status
// half matters for zombie drift rows (stage DELIVERED/PICKED_UP but status
// never flipped to 'completed') — such a row is STILL covered by the
// duplicate-guard unique index, so tiering it amber would offer an override
// the backend (and the index) will reject. It must tier in-flight (hard block).
function isTerminalCompleted(link: OrderLink): boolean {
  return (
    TERMINAL_COMPLETED_STAGES.has(link.workflowStage)
    && link.orderStatus === COMPLETED_STATUS
  );
}

/** Client mirror of backend `isDispatchedStage(stage)`. */
export function isDispatchedStage(stage: string): boolean {
  return DISPATCHED_STAGES.has(stage);
}

/**
 * Classify a link into its tier. Mirrors backend `orderLinkRestrictivenessRank`:
 * cancelled < completed < in-flight < active in restrictiveness terms.
 * The 'in-flight' tier covers EVERY still-open dispatched stage — including
 * ready_for_pickup, which is not physically en route but is equally open and
 * equally blocked at the DB.
 */
export function tierForLink(link: OrderLink): OrderLinkTier {
  if (link.isCancelled) return 'cancelled';
  const dispatched = link.isDispatched || isDispatchedStage(link.workflowStage);
  if (dispatched) {
    return isTerminalCompleted(link) ? 'completed' : 'in-flight';
  }
  return 'active';
}

/**
 * Single source of truth for the tier -> colour map. Aligns with the spec's D2
 * red/amber/grey scheme (design doc §3.2, "Color-code to the three D2 tiers"):
 *   - red   — active/pre-dispatch AND still-open dispatched — D2 HARD-BLOCK, cannot re-send
 *   - amber — terminal-completed (DELIVERED/PICKED_UP) — re-send needs a reason
 *   - grey  — cancelled — OK to re-send
 * Still-open dispatched (OUT_FOR_DELIVERY / DELIVERY_MISSED / READY_FOR_PICKUP)
 * is NOT the warn tier: an en-route order would ship a second physical package
 * and a ready-for-pickup bag is still open in the pharmacy — the user must
 * cancel the open order first (spec §3.2: "D2 in-flight is a HARD-BLOCK"; the
 * row also keeps status 'in_progress', so the duplicate-guard unique index
 * still blocks the insert). It therefore shares the red active colour, not
 * amber. The D2 *verdict* still distinguishes still-open dispatched from
 * active via `orderLinkRestrictivenessRank`, but the colour telegraphs the
 * same "cannot re-send" outcome. Only the terminal-completed tier (finished
 * physical send, status 'completed', index-exempt) is the override-able amber
 * bucket.
 */
export const TIER_COLORS: Record<OrderLinkTier, string> = {
  active: '#d32f2f', // red   — live, pre-dispatch (hard-block)
  'in-flight': '#d32f2f', // red   — dispatched, en route (hard-block — cancel first)
  completed: '#ed6c02', // amber — terminal completed (re-send needs a reason)
  cancelled: '#9e9e9e', // grey  — cancelled
};

export function tierColor(tier: OrderLinkTier): string {
  return TIER_COLORS[tier];
}

/**
 * Client mirror of backend `orderLinkRestrictivenessRank()`
 * (backend-v2/src/v2/orders/v2-orders.service.ts). Lower rank = more restrictive
 * (wins). Keep in lockstep with the backend ranking.
 */
export function orderLinkRestrictivenessRank(link: OrderLink): number {
  if (link.isCancelled) return 3; // cancelled — least restrictive
  const dispatched = link.isDispatched || isDispatchedStage(link.workflowStage);
  if (dispatched) {
    // Terminal-completed (delivered/picked_up stage AND status 'completed') is
    // override-able rank 2; every other dispatched stage (incl.
    // ready_for_pickup, and zombie drift rows whose status never flipped) is
    // still open — rank 1.
    return isTerminalCompleted(link) ? 2 : 1;
  }
  return 0; // active / pre-dispatch — most restrictive
}

/**
 * Client mirror of backend `selectRepresentativeLink()`. Picks the most-restrictive
 * link, tie-breaking on `createdAt` DESC. Returns null for an empty list.
 */
export function selectRepresentativeLink(links: OrderLink[] | undefined | null): OrderLink | null {
  if (!links || links.length === 0) return null;
  return [...links].sort((a, b) => {
    const r = orderLinkRestrictivenessRank(a) - orderLinkRestrictivenessRank(b);
    if (r !== 0) return r;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  })[0];
}
